import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../../utils/brand';
import LanguageSelector from './LanguageSelector';
import { useLocalizedUrl } from '../../utils/urlHelper';
import { APP_CONFIG, CONTACT } from '../../config';
import { LogIn, Mail, Phone, ChevronDown, ArrowUpRight } from 'lucide-react';

// Cabecera (nov-2026, diseño B2B). Los menús salen de una sola definición
// (buildMenus) que usan los desplegables de escritorio y el acordeón del móvil:
// paneles rectos con filete, títulos de sección en versalitas y enlaces con una
// línea de descripción; sin iconos en cajas de color. Soluciones y Documentos
// completos solo en español (sus páginas solo existen en español); en inglés,
// Documents lleva el eCMR y la carta de porte.

const TOP_BAR = {
  es: { support: 'Soporte' },
  en: { support: 'Support' },
};

const NAVY = '#0b1d39';

const buildMenus = (lang, url) => {
  const devPortal = `/${lang}/developers/info`;
  if (lang === 'es') {
    return [
      {
        key: 'soluciones',
        label: 'Soluciones',
        sections: [
          {
            title: 'Por tipo de empresa',
            items: [
              { title: 'Cargadores', desc: 'Fabricantes, distribuidores y operadores que contratan transporte', to: '/cargadores' },
              { title: 'Transportistas y flotas', desc: 'Empresas de transporte, conductores y tráfico', to: '/transportistas' },
              { title: 'Proveedores de software', desc: 'API e integración para TMS, ERP y plataformas', to: '/proveedores-software' },
            ],
          },
        ],
        aside: { title: 'Véalo con su operativa', text: 'Una demostración con sus documentos, sin compromiso.', link: 'Reserve su demostración', to: '/contacto/demo' },
      },
      {
        key: 'plataforma',
        label: 'Plataforma',
        sections: [
          {
            title: 'Producto',
            items: [
              { title: 'Cómo funciona', desc: 'Del primer dato al archivo', to: url('/plataforma') },
              { title: 'Interoperabilidad', desc: 'API, webhooks y ficheros en cualquier formato', to: url('/interoperabilidad') },
              { title: 'API para desarrolladores', desc: 'Referencia y entorno de pruebas', href: devPortal },
            ],
          },
          {
            title: 'Confianza',
            items: [
              { title: 'Firma y evidencias', desc: 'Firma avanzada y conservación', to: url('/confianza/firma-electronica') },
              { title: 'Seguridad', desc: 'Certificación ENS, cifrado y accesos', to: url('/confianza/seguridad') },
              { title: 'Cumplimiento normativo', desc: 'Las normas que cumple cada documento', to: url('/confianza/cumplimiento-normativo') },
            ],
          },
        ],
      },
      {
        key: 'documentos',
        label: 'Documentos',
        sections: [
          {
            title: 'Documentos',
            items: [
              { title: 'eCMR', desc: 'Transporte internacional', to: url('/documentos/ecmr') },
              { title: 'Carta de porte', desc: 'Contrato, DeCA y prueba de entrega', to: url('/documentos/carta-porte') },
              { title: 'DeCA', desc: 'Documento de control del transporte nacional', to: url('/documentos/documento-control') },
              { title: 'Documento de transporte ADR', desc: 'Mercancías peligrosas, junto al DeCA', to: url('/documentos/transporte-adr') },
            ],
          },
          {
            title: 'Recursos',
            items: [
              { title: 'Artículos', desc: 'Normativa y práctica del transporte', to: '/articulos' },
            ],
          },
        ],
      },
      {
        key: 'empresa',
        label: 'Empresa',
        sections: [
          {
            title: 'BlueCMR',
            items: [
              { title: 'Quiénes somos', desc: 'Equipo, grupo Cadenia y datos de la empresa', to: url('/quienes-somos') },
              { title: 'Contacto', desc: 'Demostración, formulario o llamada', to: url('/contacto') },
            ],
          },
        ],
        contactAside: true,
      },
    ];
  }
  // Inglés (4-nov-2026): mismas secciones que en español salvo «Recursos» (los
  // artículos solo existen en español).
  return [
    {
      key: 'soluciones',
      label: 'Solutions',
      sections: [
        {
          title: 'By type of company',
          items: [
            { title: 'Shippers', desc: 'Manufacturers, distributors and operators that buy transport', to: url('/cargadores') },
            { title: 'Carriers and fleets', desc: 'Haulage companies, drivers and traffic offices', to: url('/transportistas') },
            { title: 'Software providers', desc: 'API and integration for TMS, ERP and platforms', to: url('/proveedores-software') },
          ],
        },
      ],
      aside: { title: 'See it with your operations', text: 'A demonstration with your documents, with no commitment.', link: 'Book your demonstration', to: '/contacto/demo' },
    },
    {
      key: 'plataforma',
      label: 'Platform',
      sections: [
        {
          title: 'Product',
          items: [
            { title: 'How it works', desc: 'From the first data to the archive', to: url('/plataforma') },
            { title: 'Interoperability', desc: 'API, webhooks and files in any format', to: url('/interoperabilidad') },
            { title: 'API for developers', desc: 'Reference and test environment', href: devPortal },
          ],
        },
        {
          title: 'Trust',
          items: [
            { title: 'Signature and evidence', desc: 'Advanced signature and preservation', to: url('/confianza/firma-electronica') },
            { title: 'Security', desc: 'ENS certification, encryption and access', to: url('/confianza/seguridad') },
            { title: 'Regulatory compliance', desc: 'The rules every document complies with', to: url('/confianza/cumplimiento-normativo') },
          ],
        },
      ],
    },
    {
      key: 'documentos',
      label: 'Documents',
      sections: [
        {
          title: 'Documents',
          items: [
            { title: 'eCMR', desc: 'International transport', to: url('/documentos/ecmr') },
            { title: 'Consignment note (Spain)', desc: 'Contract, control document and proof of delivery', to: url('/documentos/carta-porte') },
            { title: 'DeCA (Spain)', desc: 'Control document for domestic transport in Spain', to: url('/documentos/documento-control') },
            { title: 'ADR transport document', desc: 'Dangerous goods, together with the DeCA', to: url('/documentos/transporte-adr') },
          ],
        },
      ],
    },
    {
      key: 'empresa',
      label: 'Company',
      sections: [
        {
          title: 'BlueCMR',
          items: [
            { title: 'About us', desc: 'Team, Cadenia group and company details', to: url('/quienes-somos') },
            { title: 'Contact', desc: 'Demonstration, form or call', to: url('/contacto') },
          ],
        },
      ],
      contactAside: true,
    },
  ];
};

/** Enlace de menú: interno con `to`, externo (portal de desarrolladores) con `href`. */
const MenuLink = ({ item, onClick, compact = false }) => {
  const body = compact ? (
    <span className="flex items-center gap-1.5">
      {item.title}
      {item.href && <ArrowUpRight className="w-3.5 h-3.5 text-slate-400" />}
    </span>
  ) : (
    <>
      <span className="flex items-center gap-1.5 text-[15px] font-semibold text-slate-900 group-hover/item:text-primary-700 transition-colors">
        {item.title}
        {item.href && <ArrowUpRight className="w-3.5 h-3.5 text-slate-400 group-hover/item:text-primary-700" />}
      </span>
      <span className="mt-0.5 block text-[13px] leading-snug text-slate-500">{item.desc}</span>
    </>
  );
  const cls = compact
    ? 'block px-4 py-2.5 text-[15px] text-slate-700 hover:text-primary-700'
    : 'group/item block px-3 py-2.5 -mx-3 hover:bg-slate-50 transition-colors';
  return item.href
    ? <a href={item.href} target="_blank" rel="noopener noreferrer" className={cls} onClick={onClick}>{body}</a>
    : <Link to={item.to} className={cls} onClick={onClick}>{body}</Link>;
};

const SectionTitle = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-3">{children}</p>
);

/**
 * Desplegable de escritorio: se abre al pasar el ratón, al tabular dentro o al
 * pulsar el botón. Con estado (antes era solo CSS, group-hover/focus-within): al
 * pulsar un enlace el ratón seguía encima y el enlace con el foco, y el panel se
 * quedaba abierto en la página nueva (Jesús, 4-nov). Ahora se cierra al pulsar un
 * enlace, al cambiar de ruta, al salir el ratón o con Escape.
 */
const DesktopMenu = ({ menu, lang, url, open, onOpen, onClose }) => {
  const asideWidth = menu.aside || menu.contactAside ? 260 : 0;
  const width = menu.sections.length * 290 + asideWidth;
  const buttonRef = useRef(null);
  const close = () => {
    onClose();
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
  };
  return (
    <div
      className="relative"
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
      onFocus={onOpen}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) onClose(); }}
      onKeyDown={(e) => { if (e.key === 'Escape') { onClose(); buttonRef.current?.focus(); } }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={onOpen}
        className={`flex items-center gap-1 px-3 py-2 text-sm font-semibold transition-colors ${open ? 'text-primary-700' : 'text-slate-700 hover:text-primary-700'}`}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {menu.label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {/* pt-3: el panel empieza pegado al botón para no perder el hover en el hueco */}
      <div
        className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 transition-opacity duration-150 ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
        style={{ width }}
      >
        <div className="flex bg-white border border-slate-200 border-t-2 shadow-[0_24px_48px_-16px_rgba(11,29,57,0.28)]" style={{ borderTopColor: NAVY }}>
          {menu.sections.map((section) => (
            <div key={section.title} className="flex-1 p-6">
              <SectionTitle>{section.title}</SectionTitle>
              <div className="space-y-0.5">
                {section.items.map((item) => <MenuLink key={item.title} item={item} onClick={close} />)}
              </div>
            </div>
          ))}
          {menu.aside && (
            <div className="p-6 bg-slate-50 border-l border-slate-200" style={{ width: asideWidth }}>
              <p className="font-semibold text-slate-900">{menu.aside.title}</p>
              <p className="mt-1 text-[13px] leading-snug text-slate-500">{menu.aside.text}</p>
              <Link to={url(menu.aside.to)} onClick={close} className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-800 transition-colors" style={{ backgroundColor: NAVY }}>
                {menu.aside.link}
              </Link>
            </div>
          )}
          {menu.contactAside && (
            <div className="p-6 bg-slate-50 border-l border-slate-200" style={{ width: asideWidth }}>
              <SectionTitle>{lang === 'es' ? 'Atención telefónica' : 'Phone support'}</SectionTitle>
              <a href={CONTACT.phoneHref} className="text-lg font-semibold text-slate-900 hover:text-primary-700">{CONTACT.phoneDisplay}</a>
              <p className="mt-0.5 text-[13px] text-slate-500">{CONTACT.phoneHours[lang] || CONTACT.phoneHours.es}</p>
              <a href={`mailto:${CONTACT.email}`} className="mt-3 block text-sm font-semibold text-primary-700 hover:underline">{CONTACT.email}</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Header = () => {
  const { t, i18n } = useTranslation(['header', 'common']);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const location = useLocation();
  const isSpanish = i18n.language === 'es';
  const { getLocalizedUrl } = useLocalizedUrl();

  const lang = isSpanish ? 'es' : 'en';
  const topBar = TOP_BAR[lang];
  const menus = buildMenus(lang, getLocalizedUrl);
  const closeMobile = () => setMobileOpen(false);

  // Al cambiar de página, todo cerrado: desplegables, menú móvil y su acordeón.
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
    setMobileSection(null);
  }, [location.pathname, location.search, location.hash]);

  return (
    <>
    {/* Barra de contacto (nov-2026): fuera del <header> sticky para que solo
        se vea al cargar y se vaya con el scroll. */}
    <div className="bg-slate-50 border-b border-gray-200 text-[13px] text-slate-600">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <Phone className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span>{topBar.support}</span>
          <a href={CONTACT.phoneHref} className="font-semibold text-slate-900 hover:text-primary-700 whitespace-nowrap">
            {CONTACT.phoneDisplay}
          </a>
        </div>
        <a href={`mailto:${CONTACT.email}`} className="hidden sm:flex items-center gap-2 hover:text-primary-700 whitespace-nowrap">
          <Mail className="w-3.5 h-3.5 text-slate-400" />
          {CONTACT.email}
        </a>
      </div>
    </div>
    <header className="bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            {/* Logotipo: solo el wordmark, sin el icono de la carpeta con la B */}
            <Link to={isSpanish ? '/' : '/en'} className="hover:opacity-80 transition-opacity" aria-label={`${BRAND.shortName} — ${isSpanish ? 'inicio' : 'home'}`}>
              <span className="text-[1.75rem] leading-none tracking-tight text-primary-700" style={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 900 }}>
                {BRAND.shortName}
              </span>
            </Link>
          </div>

          <nav className="hidden lg:flex items-center gap-1" aria-label={isSpanish ? 'Principal' : 'Main'}>
            {menus.map((menu) => (
              <DesktopMenu
                key={menu.key}
                menu={menu}
                lang={lang}
                url={getLocalizedUrl}
                open={openMenu === menu.key}
                onOpen={() => setOpenMenu(menu.key)}
                onClose={() => setOpenMenu((k) => (k === menu.key ? null : k))}
              />
            ))}
          </nav>

          <div className="hidden lg:flex items-center space-x-4">
            <LanguageSelector />
            <Link to={getLocalizedUrl('/contacto')} className="btn btn-outline rounded-none shadow-none text-sm flex items-center gap-1.5 border border-primary-700 text-primary-700 hover:bg-primary-50">
              <Mail className="w-4 h-4" />
              {t('header:nav-contacto')}
            </Link>
            <a href={APP_CONFIG.PRIVATE_APP_URL} className="btn btn-primary rounded-none shadow-none text-sm flex items-center gap-1.5 bg-primary-700 hover:bg-primary-800">
              <LogIn className="w-4 h-4" />
              {t('header:acceder')}
            </a>
          </div>

          <div className="lg:hidden">
            <button
              onClick={() => setMobileOpen((o) => !o)}
              className="p-2 text-slate-700 hover:text-slate-900"
              aria-label={isSpanish ? 'Abrir el menú de navegación' : 'Open navigation menu'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white" style={{ maxHeight: 'calc(100vh - 4rem)', display: 'flex', flexDirection: 'column' }}>
          <nav className="pb-4 overflow-y-auto flex-1" aria-label={isSpanish ? 'Principal' : 'Main'}>
            {menus.map((menu) => {
              const open = mobileSection === menu.key;
              return (
                <div key={menu.key} className="border-b border-slate-200">
                  <button
                    onClick={() => setMobileSection(open ? null : menu.key)}
                    className="w-full flex justify-between items-center px-4 py-3.5 text-[15px] font-semibold text-slate-900"
                    aria-expanded={open}
                  >
                    {menu.label}
                    <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
                  </button>
                  {open && (
                    <div className="pb-3 bg-slate-50">
                      {menu.sections.map((section) => (
                        <div key={section.title} className="pt-3">
                          <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{section.title}</p>
                          {section.items.map((item) => <MenuLink key={item.title} item={item} onClick={closeMobile} compact />)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="px-4 pt-4 space-y-3">
              <div className="flex justify-center">
                <LanguageSelector />
              </div>
              <Link to={getLocalizedUrl('/contacto')} onClick={closeMobile} className="btn btn-outline rounded-none shadow-none w-full justify-center flex items-center gap-1.5 border border-primary-700 text-primary-700">
                <Mail className="w-4 h-4" />
                {t('header:nav-contacto')}
              </Link>
              <a href={APP_CONFIG.PRIVATE_APP_URL} className="btn btn-primary rounded-none shadow-none w-full justify-center flex items-center gap-1.5 bg-primary-700 hover:bg-primary-800">
                <LogIn className="w-4 h-4" />
                {t('header:acceder')}
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
    </>
  );
};

export default Header;
